import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/core/hooks/useAuth";
import { acessoService } from "@/core/services/acessoService";
import {
  visitantesService,
  type DadosFamiliar,
  type DadosVisitante,
} from "@/modules/visitantes/services/visitantesService";

const CHAVE = ["visitantes"];
const fichaKey = (id: string) => ["visitantes", "ficha", id];

/**
 * Qualquer mutação muda a lista, as fichas e os informados (todos sob a chave
 * "visitantes"). Roda também em caso de erro: um conflito de versão (40001)
 * significa que a tela está velha.
 */
function useInvalidarVisitantes() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: CHAVE });
}

export type PapelVisitantes = "gestao" | "admin" | "lider";

/**
 * Papel de quem está na tela — só decide o que MOSTRAR; quem decide o que PODE é
 * o banco (`_visitante_papel`). Falha na checagem = sem poder de gestão (fail-closed).
 *  - gestao: líder/gestor do ministério responsável (lê e escreve tudo);
 *  - admin: administrador sem papel no elo (lê tudo, não escreve);
 *  - lider: líder/gestor de outro ministério (consulta reduzida, contato e Visitantes pendentes).
 */
export function usePapelVisitantes(): { papel: PapelVisitantes | null; carregando: boolean } {
  const { ehAdminPlataforma, carregando: carregandoAuth } = useAuth();
  const { data: gerencia, isLoading } = useQuery({
    queryKey: ["visitantes-pode-gerenciar"],
    queryFn: () => acessoService.podeGerenciarModulo("visitantes"),
  });
  if (isLoading || carregandoAuth) return { papel: null, carregando: true };
  if (gerencia) return { papel: "gestao", carregando: false };
  if (ehAdminPlataforma) return { papel: "admin", carregando: false };
  return { papel: "lider", carregando: false };
}

/**
 * Quem pode cadastrar Visitante pendente: líder/gestor de outro ministério — inclusive o
 * administrador que também lidera ou gerencia algum ministério (ele continua com a visão de
 * administrador; só ganha o cadastro de pendentes). Só decide o que MOSTRAR; o banco confere
 * (`_visitante_pode_informar`).
 */
export function usePodeInformar(): { podeInformar: boolean; carregando: boolean } {
  const { papel, carregando } = usePapelVisitantes();
  const { data: ministerios, isLoading } = useMinisteriosQueLidero(papel === "admin");
  if (carregando) return { podeInformar: false, carregando: true };
  if (papel === "lider") return { podeInformar: true, carregando: false };
  if (papel === "admin") {
    return { podeInformar: (ministerios?.length ?? 0) > 0, carregando: isLoading };
  }
  return { podeInformar: false, carregando: false };
}

export function useVisitantes(habilitado = true) {
  return useQuery({
    queryKey: [...CHAVE, "lista"],
    queryFn: visitantesService.listar,
    enabled: habilitado,
  });
}

export function useFichaVisitante(id: string | undefined) {
  return useQuery({
    queryKey: fichaKey(id ?? ""),
    queryFn: () => visitantesService.ficha(id as string),
    enabled: !!id,
    retry: false,
  });
}

/** Visitantes pendentes para avaliação — ministério responsável e administrador. */
export function useIndicacoes(habilitado: boolean) {
  return useQuery({
    queryKey: [...CHAVE, "informados"],
    queryFn: visitantesService.indicacoes,
    enabled: habilitado,
  });
}

/** Meus pendentes — líder/gestor de outro ministério (só o que ele mesmo cadastrou). */
export function useMinhasIndicacoes(habilitado: boolean) {
  return useQuery({
    queryKey: [...CHAVE, "meus-informados"],
    queryFn: visitantesService.minhasIndicacoes,
    enabled: habilitado,
  });
}

export function useMinisteriosQueLidero(habilitado: boolean) {
  const { pessoa } = useAuth();
  return useQuery({
    queryKey: [...CHAVE, "ministerios-que-lidero", pessoa?.id ?? ""],
    queryFn: () => visitantesService.ministeriosQueLidero(pessoa?.id as string),
    enabled: habilitado && !!pessoa?.id,
  });
}

export function useMinisteriosResponsaveis(habilitado: boolean) {
  return useQuery({
    queryKey: [...CHAVE, "ministerios-responsaveis"],
    queryFn: visitantesService.ministeriosResponsaveis,
    enabled: habilitado,
  });
}

export function useCriarVisitante() {
  const invalidar = useInvalidarVisitantes();
  return useMutation({
    mutationFn: (v: { dados: DadosVisitante; consentimento: boolean }) =>
      visitantesService.criar(v.dados, v.consentimento),
    onSettled: invalidar,
  });
}

/** Cadastro em família (etapa 1 + etapa 2 de uma vez). */
export function useCriarFamilia() {
  const invalidar = useInvalidarVisitantes();
  return useMutation({
    mutationFn: (v: {
      principal: DadosVisitante;
      membros: DadosFamiliar[];
      consentimento: boolean;
    }) => visitantesService.criarFamilia(v.principal, v.membros, v.consentimento),
    onSettled: invalidar,
  });
}

export function useRegistrarConsentimento() {
  const invalidar = useInvalidarVisitantes();
  return useMutation({
    mutationFn: (v: { id: string; versao: number }) =>
      visitantesService.registrarConsentimento(v.id, v.versao),
    onSettled: invalidar,
  });
}

export function useRetirarConsentimento() {
  const invalidar = useInvalidarVisitantes();
  return useMutation({
    mutationFn: (v: { id: string; versao: number }) =>
      visitantesService.retirarConsentimento(v.id, v.versao),
    onSettled: invalidar,
  });
}

export function useEditarVisitante() {
  const invalidar = useInvalidarVisitantes();
  return useMutation({
    mutationFn: (v: { id: string; versao: number; dados: DadosVisitante }) =>
      visitantesService.editar(v.id, v.versao, v.dados),
    onSettled: invalidar,
  });
}

export function useRegistrarVisita() {
  const invalidar = useInvalidarVisitantes();
  return useMutation({
    mutationFn: (v: { id: string; data: string }) =>
      visitantesService.registrarVisita(v.id, v.data),
    onSettled: invalidar,
  });
}

export function useRegistrarContato() {
  const invalidar = useInvalidarVisitantes();
  return useMutation({
    mutationFn: (v: { id: string; data: string; canal: string; observacao: string }) =>
      visitantesService.registrarContato(v.id, v.data, v.canal, v.observacao),
    onSettled: invalidar,
  });
}

export function useExcluirVisitante() {
  const invalidar = useInvalidarVisitantes();
  return useMutation({
    mutationFn: (v: { id: string; versao: number }) => visitantesService.excluir(v.id, v.versao),
    onSettled: invalidar,
  });
}

export function useMarcarInformacoesLidas() {
  const invalidar = useInvalidarVisitantes();
  return useMutation({
    mutationFn: (id: string) => visitantesService.marcarInformacoesLidas(id),
    onSettled: invalidar,
  });
}

export function usePassarInformacao() {
  const invalidar = useInvalidarVisitantes();
  return useMutation({
    mutationFn: (v: { pessoaId: string; texto: string; ministerioId?: string }) =>
      visitantesService.passarInformacao(v.pessoaId, v.texto, v.ministerioId),
    onSettled: invalidar,
  });
}

export function useInformarVisitante() {
  const invalidar = useInvalidarVisitantes();
  return useMutation({
    mutationFn: (v: {
      dados: DadosVisitante;
      familiares: DadosFamiliar[];
      consentimento: boolean;
    }) => visitantesService.informar(v.dados, v.familiares, v.consentimento),
    onSettled: invalidar,
  });
}

export function useEditarInformado() {
  const invalidar = useInvalidarVisitantes();
  return useMutation({
    mutationFn: (v: {
      id: string;
      versao: number;
      dados: DadosVisitante;
      familiares: DadosFamiliar[];
    }) => visitantesService.editarInformado(v.id, v.versao, v.dados, v.familiares),
    onSettled: invalidar,
  });
}

export function useMarcarAvisoLido() {
  const invalidar = useInvalidarVisitantes();
  return useMutation({
    mutationFn: (id: string) => visitantesService.marcarAvisoLido(id),
    onSettled: invalidar,
  });
}

export function useAdicionarInformado() {
  const invalidar = useInvalidarVisitantes();
  return useMutation({
    mutationFn: (v: { id: string; versao: number; dados: DadosVisitante }) =>
      visitantesService.adicionarInformado(v.id, v.versao, v.dados),
    onSettled: invalidar,
  });
}

export function useDescartarInformado() {
  const invalidar = useInvalidarVisitantes();
  return useMutation({
    mutationFn: (v: { id: string; versao: number; motivo: string }) =>
      visitantesService.descartarInformado(v.id, v.versao, v.motivo),
    onSettled: invalidar,
  });
}

export function useRegistrarVisitaDoInformado() {
  const invalidar = useInvalidarVisitantes();
  return useMutation({
    mutationFn: (v: { id: string; versao: number; visitanteId: string }) =>
      visitantesService.registrarVisitaDoInformado(v.id, v.versao, v.visitanteId),
    onSettled: invalidar,
  });
}
